/**
 * @fileoverview The top of a letterbox city plan: counters and a per-week
 * bar chart, all from `missionStats`. Replaces MissionsOverview on these
 * plans, whose map showed nothing (letterbox missions have no location)
 * and whose charts needed every mission loaded.
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import Grid from '@material-ui/core/Grid'
import styled from 'styled-components'

import DetailsSection from 'Components/DetailsSection/DetailsSection'
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator'
import BarChart from 'Containers/StatsOverview/components/CampaignPerforming/Charts/BarChart'
import GenerateButton from 'Containers/Campaigns/RosterView/components/GenerateButton/GenerateButton'
import { PATHS } from 'Config/Routing/paths'
import { detailsItemsFromStats } from 'Utils/CityPlan'
import { MISSION_STATUS_ENUM } from 'shared/constants'

const Wrapper = styled.div`
  margin-bottom: 16px;

  .actions {
    display: flex;
    justify-content: flex-end;
    margin: 8px 0;
  }

  .error {
    color: #c8102e;
    font-size: 13px;
  }
`

/**
 * The per-week chart's series: drafts, signed-off drafts and published, one
 * bar per ISO week. Pure so it can be read at a glance.
 * @param {?object} stats
 * @returns {{categories: Array<string>, series: Array<object>}}
 */
export const weekChartData = stats => {
  const weeks = ((stats && stats.weeks) || []).filter(
    week => !week.isEmptyPlaceholder || week.total > 0,
  )
  const count = (week, status) => {
    const entry = (week.byStatus || []).find(item => item.status === status)
    return entry ? entry.count : 0
  }
  return {
    categories: weeks.map(week => `W${week.isoWeek}`),
    series: [
      {
        name: 'Draft',
        data: weeks.map(
          week => count(week, MISSION_STATUS_ENUM.DRAFT) - week.readyCount,
        ),
      },
      { name: 'Ready to publish', data: weeks.map(week => week.readyCount) },
      {
        name: 'Published',
        data: weeks.map(
          week => week.total - count(week, MISSION_STATUS_ENUM.DRAFT),
        ),
      },
    ],
  }
}

const CityPlanStatsHeader = ({
  stats,
  loading,
  error,
  isShowMissionGenerateButton,
}) => {
  const history = useHistory()
  const items = useMemo(() => detailsItemsFromStats(stats), [stats])
  const chart = useMemo(() => weekChartData(stats), [stats])

  return (
    <Wrapper>
      {loading && !stats ? (
        <LoadingIndicator />
      ) : (
        <DetailsSection items={items} />
      )}
      {error && (
        <div className="error" role="alert">
          Could not load the mission counts: {error}
        </div>
      )}
      {isShowMissionGenerateButton && (
        <div className="actions">
          <GenerateButton
            hidden={false}
            onClick={() =>
              history.push(
                `${history.location.pathname}${PATHS.cityPlan.aiRoster}`,
              )
            }
          />
        </div>
      )}
      {chart.categories.length > 0 && (
        <Grid container>
          <Grid item xs={12}>
            <BarChart
              key={`chart-weeks-${chart.categories.length}`}
              title="Missions per week"
              yAxisTitle="# of Missions"
              series={chart.series}
              tooltipValueSuffix=" missions"
              categories={chart.categories}
            />
          </Grid>
        </Grid>
      )}
    </Wrapper>
  )
}

CityPlanStatsHeader.propTypes = {
  stats: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  isShowMissionGenerateButton: PropTypes.bool,
}

CityPlanStatsHeader.defaultProps = {
  stats: null,
  loading: false,
  error: null,
  isShowMissionGenerateButton: false,
}

export default CityPlanStatsHeader
